import * as React from 'react';
import { LayoutChangeEvent, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { doReportingLoad, doSetSidebar, SET_GENERIC } from 'store/actions';
import { useSelector } from 'hooks/useSelector';
import {
  selectRptLoadStatus,
  selectRptReportPages,
  selectRptReportPagesSaved,
} from 'store/remote/selectors';
import { PageSidebar } from './PageSidebar';
import { createDrawerNavigator } from '@react-navigation/drawer';
import { theme } from 'lib/theme';
import { useFocusEffect } from '@react-navigation/native';
import { useScreenSize } from 'hooks/useScreenSize';
import { ScreenSize } from 'lib/constants';
import { SettingsScreen } from 'screens/SettingsScreen/SettingsScreen';
import { genericSidebarWidth } from 'types/types';
import { selectGeneric, selectSidebar } from 'store/app/selectors';
import { MainDrawerParamList } from 'components/Navigation/types';
import { ProfileScreen } from 'screens/ProfileScreen/ProfileScreen';
import { PageSwitch } from './PageSwitch';

const Drawer = createDrawerNavigator<MainDrawerParamList>();

export const PageStack: React.FC = () => {
  const dispatch = useDispatch();
  const loadStatus = useSelector(selectRptLoadStatus);
  const screenSize = useScreenSize();
  const sidebar = useSelector(selectSidebar);
  const reportPagesSaved = useSelector(selectRptReportPagesSaved);
  const reportPages = useSelector(selectRptReportPages);

  const currentLoadedStatusRef = React.useRef<'error' | 'loaded'>(null);
  const drawerStyle = 'permanent';

  if (loadStatus === 'error' || loadStatus === 'loaded') {
    currentLoadedStatusRef.current = loadStatus;
  }

  const handleNavigatorLayout = React.useCallback(
    (event: LayoutChangeEvent) => {
      if (event.nativeEvent.layout.width < 600) {
        dispatch(
          doSetSidebar({
            canBePinned: false,
          }),
        );
      } else if (!sidebar.canBePinned) {
        dispatch(
          doSetSidebar({
            canBePinned: true,
          }),
        );
      }
      if (screenSize === ScreenSize.small) {
        dispatch(
          SET_GENERIC({
            key: genericSidebarWidth,
            value: event.nativeEvent.layout.width - theme.padding * 3,
            temp: false,
          }),
        );
      }
    },
    [dispatch, screenSize, sidebar.canBePinned],
  );

  const drawerWidth =
    (useSelector((state) =>
      selectGeneric(state, genericSidebarWidth),
    ) as number) || 300;

  const handleDrawerLayout = React.useCallback(
    (event: LayoutChangeEvent) => {
      if (event.nativeEvent.layout.width) {
        dispatch(
          SET_GENERIC({
            key: genericSidebarWidth,
            value: event.nativeEvent.layout.width,
            temp: false,
          }),
        );
      }
    },
    [dispatch],
  );

  useFocusEffect(
    React.useCallback(() => {
      if (loadStatus === undefined) {
        dispatch(doReportingLoad());
      }
    }, [dispatch, loadStatus]),
  );

  const returnDrawContent = React.useCallback(
    () => (
      <PageSidebar
        onLayout={handleDrawerLayout}
        width={drawerWidth}
        resizable={sidebar.isPinned}
        forceResizable={sidebar.isPinned}
      ></PageSidebar>
    ),
    [handleDrawerLayout, drawerWidth, sidebar.isPinned],
  );

  return (
    <View onLayout={handleNavigatorLayout} style={{ flex: 1 }}>
      <Drawer.Navigator
        drawerContent={returnDrawContent}
        defaultStatus="closed"
        screenOptions={{
          headerShown: false,
          drawerType:
            sidebar.isPinned && sidebar.canBePinned ? drawerStyle : 'front',
          overlayColor: theme.colors.backdrop,
          drawerStyle: {
            width: drawerWidth,
            backgroundColor: 'transparent',
            borderRightWidth: 0,
          },
          sceneStyle: {
            backgroundColor: 'transparent',
          },
          swipeEdgeWidth: 0,
          swipeEnabled: false,
        }}
        initialRouteName={reportPagesSaved[0]?.key || 'UserProfile'}
      >
        {reportPagesSaved.map((page) => (
          <Drawer.Screen
            key={page.key}
            name={page.key}
            component={PageSwitch}
            initialParams={page}
          />
        ))}
        {reportPages.map((page) => (
          <Drawer.Screen
            key={page.key}
            name={page.key}
            component={PageSwitch}
            initialParams={page}
          />
        ))}
        <Drawer.Screen
          name={'UserProfile'}
          component={ProfileScreen}
          initialParams={{}}
        />
        <Drawer.Screen
          name={'AppSettings'}
          component={SettingsScreen}
          initialParams={{}}
        />
      </Drawer.Navigator>
    </View>
  );
};
